<script setup lang="ts">
import { computed, onMounted, ref, toRaw, watch } from 'vue'
import { Icon } from '@iconify/vue'
import './account_card.scss'
import { beautyPhoneNumber, getFirstCharacterName } from '@/utils/helper.ts'
import BrokerDetail from '@/modules/broker/views/BrokerDetail.vue'
import AccountDetail from '@/modules/account/views/AccountDetail.vue'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import BrokerAPI from '@/modules/broker/constants/broker.api.uri.const.ts'

interface Props {
  id: string,
  detail?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['update'])

const showDetail = ref<boolean>(false)
const obj = ref<any>({
  id: '',
  code: '',
  name: '',
  national_id_no: '',
  address: '',
  phone: '',
  email: '',
  tax_id: '',
  bank_name: '',
  bank_account_name: '',
  bank_account_number: '',
})

const formData = ref<any>({
  tax_id: '',
  bank_name: '',
  bank_account_name: '',
  bank_account_number: '',
})

const handleShowDetail = () => {
  showDetail.value = true
}

const fetchData = async (id: string) => {
  try{
    const res = await APIClient.get(`/accounts/${id}`)

    if(res.data?.data?.id) {
      const item = res.data.data
      obj.value = {
        id: item.id,
        code: item.code,
        name: item.full_name,
        national_id_no: item.kyc_information?.national_id_number || '',
        address: item.kyc_information?.address || '',
        phone: item.phone_number || '',
        email: item.email || '',
        customer_id: item.customer_profile?.id || '',
        tax_id: item?.customer_profile?.tax_id || '',
        bank_name: item?.customer_profile?.bank_name || '',
        bank_account_name: item?.customer_profile?.bank_account_name || '',
        bank_account_number: item?.customer_profile?.bank_account_number || '',
      }
    }
  } catch (e) {
    console.error(e)
  }
}

const handleUpdate = async() => {
  obj.value = {...toRaw(obj.value), ...toRaw(formData.value)}
  console.log(72, obj.value, formData.value)
  try{
    const res = await APIClient.post(`customers`, {
      ...formData.value,
      user_id: obj.value?.id,
      profile_type: "individual",
    })

    console.log(res)
  } catch (e) {
    console.error(e)
  }
  emit('update', formData.value)
}

onMounted(()=>{
  fetchData(props.id)
})

watch(()=>props.id, ()=>{
  fetchData(props.id)
}, {immediate: true})

</script>

<template>
  <div class="flex flex-1 items-start gap-[7px] account-card customer rounded-[8px]">
    <div class="flex flex-1 flex-col">
      <div class="text-[12px] font-bold full-name">{{obj.name}}</div>
      <div class="flex flex-1 flex-col w-full" v-if="!props.detail">
        <div class="flex gap-[3px] items-center">
          <Icon icon="mage:qr-code-fill" color="#c9c9c9"/> #{{obj.code}}
        </div>
        <div class="flex flex-col md:flex-row gap-2">
          <div class="flex flex-1 gap-[3px] items-center">
            <Icon icon="zondicons:phone" color="#c9c9c9"/> {{beautyPhoneNumber(obj.phone)}}
          </div>
          <div class="flex flex-row flex-1 gap-[3px] items-center">
            <Icon icon="ic:baseline-email" color="#c9c9c9"/>
            <span class="flex-1">{{obj.email}}</span>
          </div>
        </div>
        <div class="flex flex-col md:flex-row gap-2">
          <div class="flex flex-1 gap-[3px] items-center">
            <Icon icon="zondicons:phone" color="#c9c9c9"/> {{beautyPhoneNumber(obj.phone)}}
          </div>
          <div class="flex flex-row flex-1 gap-[3px] items-center">
            <Icon icon="ic:baseline-email" color="#c9c9c9"/>
            <span class="flex-1">{{obj.email}}</span>
          </div>
        </div>
      </div>
      <div class="flex flex-col w-full mt-3" v-else>
        <div class="mt-2 flex flex-row w-full">
          <div class="flex-1">CCCD:</div>
          <div class="flex-2 font-[600]">{{obj.national_id_no}}</div>
        </div>
        <div class="mt-2 flex flex-row w-full">
          <div class="flex-1">Địa chỉ:</div>
          <div class="flex-2 font-[600]">{{obj.address}}</div>
        </div>
        <div class="mt-2 flex flex-row w-full">
          <div class="flex-1">SĐT:</div>
          <div class="flex-2 font-[600]">{{obj.phone}}</div>
        </div>
        <div class="mt-2 flex flex-row w-full">
          <div class="flex-1">Email:</div>
          <div class="flex-2 font-[600]">{{obj.email}}</div>
        </div>
        <Divider/>
        <div class="mt-2 flex flex-row w-full">
          <div class="flex-1">MST:</div>
          <div class="flex-2 font-[600]" v-if="obj.tax_id">{{obj.tax_id}}</div>
          <div class="flex-2 font-[600]" v-else>
            <InputText v-model="formData.tax_id" class="form-control"/>
          </div>

        </div>
        <div class="mt-2 flex flex-row w-full">
          <div class="flex-1">Ngân hàng:</div>
          <div class="flex-2 font-[600]" v-if="obj.bank_name">{{obj.bank_name}}</div>
          <div class="flex-2 font-[600]" v-else>
            <InputText v-model="formData.bank_name" class="form-control"/>
          </div>
        </div>
        <div class="mt-2 flex flex-row w-full">
          <div class="flex-1">Tài khoản:</div>
          <div class="flex-2 font-[600]" v-if="obj.bank_account_number">{{obj.bank_account_number}}</div>
          <div class="flex-2 font-[600]" v-else>
            <InputText v-model="formData.bank_account_number" class="form-control"/>
          </div>
        </div>
        <div class="mt-2 flex flex-row w-full">
          <div class="flex-1">Chủ tài khoản:</div>
          <div class="flex-2 font-[600]" v-if="obj.bank_account_name">{{obj.bank_account_name}}</div>
          <div class="flex-2 font-[600]" v-else>
            <InputText v-model="formData.bank_account_name" class="form-control"/>
          </div>
        </div>
        <div class="mt-2 flex flex-row w-full">
          <div class="flex-1"></div>
          <div class="flex-2 font-[600]" v-if="!obj.tax_id || !obj.bank_name || !obj.bank_account_name || !obj.bank_account_number">
            <button @click="handleUpdate" type="button" class="btn btn-cs">Cập nhật</button>
          </div>
        </div>
      </div>
    </div>
    <div class="detail-btn" @click="handleShowDetail"><Icon icon="bi:info-circle-fill" /> chi tiết</div>
  </div>
  <Drawer v-model:visible="showDetail" header="Thông tin Khách hàng" position="right">
    <div class="drawer-80vw">
      <AccountDetail :id="obj?.id"/>
    </div>
  </Drawer>
</template>

<style scoped>

</style>
